import { first, isEqual, map, omitBy, size, sumBy, values } from "lodash";
import type { FilterOptionOption } from "react-select";

import type { GoodsInput } from "../../gql/graphql";
import {
  getImportExportSettingsFromMinimalBooking,
  getImportExportSettingsFromRoute,
} from "../../use-cases/import-export/util/ImportExportSettingsGetters";
import type { GoodsType } from "../graphql/fragments/gql/GoodsType";
import { isAnimalLivestockType } from "../utils/animalsUtils";
import { getGuid } from "../utils/guidUtils";
import type { MinimalBooking } from "../utils/minimalBooking";
import type { Animal, DropdownOption } from "../utils/optionTransformers";

export interface GoodsValidationError {
  description?: string;
  type?: string;
  weight?: string;
}

export interface GoodsItem {
  code: string;
  description: string;
  goodsType: GoodsType | null;
  id: string;
  weight: number | undefined;
}

export type GoodsItems = Record<string, GoodsItem>;

export function getNewGoodsItem(): GoodsItem {
  const id = getGuid();

  return {
    id,
    code: "",
    goodsType: null,
    description: "",
    weight: undefined,
  };
}

export function updateGoodsItemDescription(
  goodsItem: GoodsItem,
  description: string,
): GoodsItem {
  return {
    ...goodsItem,
    description,
  };
}

export function isDefaultGoodsDescription(goodsItem: GoodsItem) {
  return (
    goodsItem.goodsType &&
    goodsItem.goodsType.description === goodsItem.description
  );
}

export function updateGoodsItemType(
  goodsItem: GoodsItem,
  type: GoodsType,
): GoodsItem {
  const shouldOverrideGoodsTypeDescription =
    !goodsItem.description ||
    goodsItem.goodsType == null ||
    isDefaultGoodsDescription(goodsItem);

  const newItem: GoodsItem = { ...goodsItem, goodsType: type, code: type.code };

  if (shouldOverrideGoodsTypeDescription) {
    newItem.description = type.description;
  }

  return newItem;
}

export function updateGoodsItemWeight(
  goodsItem: GoodsItem,
  weight: number | undefined,
): GoodsItem {
  return {
    ...goodsItem,
    weight,
  };
}

export function removeGoodsItem(
  goodsItems: GoodsItems,
  id: string,
): GoodsItems {
  const newGoodsItems = { ...goodsItems };
  delete newGoodsItems[id];
  return newGoodsItems;
}

export function setGoodsItem(
  goodsItems: GoodsItems,
  goodsItem: GoodsItem,
): GoodsItems {
  const newGoodsItems = { ...goodsItems };
  newGoodsItems[goodsItem.id] = goodsItem;
  return newGoodsItems;
}

export function getTotalWeightFromGoodsItems(
  goodsItems: GoodsItems | GoodsItem[] | null | undefined,
): number {
  const items = Array.isArray(goodsItems) ? goodsItems : values(goodsItems);

  return sumBy(items, (item) => item.weight ?? 0);
}

export const noneGoodsTypeOption: DropdownOption<GoodsType> = {
  label: "None",
  value: "",
  data: { id: "", code: "", description: "", isValid: true },
};

export function transformGoodsToInput(goods: GoodsItems): GoodsInput[] {
  return map(goods, (g) => ({
    id: g.id,
    weight: g.weight ?? 0,
    code: g.code,
    description: g.description.trim(),
  }));
}

export function isGoodsEmpty(goodsItems: GoodsItems) {
  if (size(goodsItems) === 1) {
    const goodsItem = first(values(goodsItems));

    return (
      goodsItem &&
      !goodsItem.goodsType &&
      !goodsItem.weight &&
      !goodsItem.description
    );
  }

  return size(goodsItems) === 0;
}

export function getRegistrationNumberAlertGoodsItem(
  weight: number,
  options?: { useGeneralCargo: boolean },
) {
  const goodsItem = getNewGoodsItem();
  if (options?.useGeneralCargo) {
    goodsItem.code = "GE";
    goodsItem.description = "General Cargo";
  }

  goodsItem.weight = weight;

  return {
    [goodsItem.id]: goodsItem,
  };
}

export function getAnimalGoodsItem(
  animalGoodsType: GoodsType,
  currentAnimalGoodsItems: GoodsItem[],
  livestockType: Animal,
): GoodsItem {
  const animalGoodsItem =
    currentAnimalGoodsItems.length === 0
      ? getNewGoodsItem()
      : currentAnimalGoodsItems[0];

  return {
    ...animalGoodsItem,
    code: animalGoodsType.code,
    goodsType: animalGoodsType,
    description: livestockType.value === "BLOOD" ? livestockType.label : "",
  };
}

export function getGoodsItemsWithoutAnimals(
  goodsItems: GoodsItems,
): GoodsItems {
  return omitBy(goodsItems, (goodsItem) => goodsItem.goodsType?.code === "ANI");
}

export function getAnimalItemsFromGoodsItems(
  goodsItems: GoodsItems,
): GoodsItem[] {
  return values(goodsItems).filter((g) => g.goodsType?.code === "ANI");
}

export function getGoodsItemsWithAnimals(
  goodsTypes: GoodsType[],
  goodsItems: GoodsItems,
  livestockType: Animal,
) {
  const animalGoodsType = goodsTypes.find((g) => g.code === "ANI");
  const currentAnimalGoodsItems = getAnimalItemsFromGoodsItems(goodsItems);

  if (!animalGoodsType || currentAnimalGoodsItems.length > 1) {
    return goodsItems;
  }

  const animalGoodsItem = getAnimalGoodsItem(
    animalGoodsType,
    currentAnimalGoodsItems,
    livestockType,
  );

  return isGoodsEmpty(goodsItems)
    ? { [animalGoodsItem.id]: animalGoodsItem }
    : { ...goodsItems, [animalGoodsItem.id]: animalGoodsItem };
}

export function getGoodsItemsForLivestockType(
  goodsTypes: GoodsType[],
  goodsItems: GoodsItems,
  livestockType: Animal,
): GoodsItems {
  return isAnimalLivestockType(livestockType)
    ? getGoodsItemsWithAnimals(goodsTypes, goodsItems, livestockType)
    : getGoodsItemsWithoutAnimals(goodsItems);
}

export function filterGoodsTypes(
  option: FilterOptionOption<DropdownOption<GoodsType>>,
  hasAnimals: boolean,
) {
  if (option.value === "ANI" && !hasAnimals) {
    return false;
  }
  return option.data.data.isValid;
}

export function getIsGoodsItemEdited(initial: GoodsItem, edited: GoodsItem) {
  return !isEqual(initial, edited);
}

export function getGoodsItemPlaceholder(
  goodsItem: GoodsItem | undefined,
): string | undefined {
  return goodsItem?.goodsType?.code === "ANI" ? "Enter animal type" : undefined;
}

export function getGoodsGridCellLabel(goods: GoodsItem[]): string | undefined {
  if (goods.length === 0) {
    return undefined;
  }

  return (
    goods[0].description + (goods.length > 1 ? " +" + (goods.length - 1) : "")
  );
}

export function getShowEnsGoodsBanner(
  minimalBooking: MinimalBooking,
  options?: { ensConfirmed: boolean },
) {
  const importSettings = getImportExportSettingsFromMinimalBooking(
    minimalBooking,
    getImportExportSettingsFromRoute(minimalBooking?.route, "importReference"),
  );

  return (
    !options?.ensConfirmed &&
    Boolean(importSettings?.showEnsLink && !importSettings?.ensMandatory)
  );
}
